import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import {
  BarChart,
  PieChart,
  ShowChart,
  Speed,
  SmartToy,
} from "@mui/icons-material";
import { DashboardWidget, WIDGET_TYPES } from "../../types/dashboard/DashboardConfig";
import { v4 as uuidv4 } from "uuid";

interface AddWidgetDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (widget: DashboardWidget) => void;
  onAskArchie: () => void;
}

const WIDGET_TEMPLATES = [
  {
    label: "Bar Chart",
    description: "Aggregate interactions by a field (e.g. agent, type, direction)",
    icon: <BarChart />,
    chartType: "bar",
    aggregationType: "terms",
    field: "interactionType",
    w: 6,
    h: 4,
  },
  {
    label: "Pie Chart",
    description: "Show proportional breakdown by a field",
    icon: <PieChart />,
    chartType: "pie",
    aggregationType: "terms",
    field: "interactionType",
    w: 4,
    h: 4,
  },
  {
    label: "Line Chart (Over Time)",
    description: "Show trends over time with a date histogram",
    icon: <ShowChart />,
    chartType: "line",
    aggregationType: "dateHistogram",
    field: "startTime",
    w: 6,
    h: 4,
  },
  {
    label: "KPI Counter",
    description: "Show a single count metric with optional filters",
    icon: <Speed />,
    chartType: "kpi",
    aggregationType: "count",
    field: "startTime",
    w: 3,
    h: 2,
  },
];

export default function AddWidgetDialog({
  open,
  onClose,
  onAdd,
  onAskArchie,
}: Readonly<AddWidgetDialogProps>) {
  const handleTemplateClick = (template: (typeof WIDGET_TEMPLATES)[number]) => {
    const widget: DashboardWidget = {
      id: uuidv4(),
      type: WIDGET_TYPES.CUSTOM_AGGREGATE,
      title: `${template.label}`,
      layout: {
        x: 0,
        y: Infinity, // react-grid-layout places at bottom
        w: template.w,
        h: template.h,
        minW: 2,
        minH: 2,
      },
      config: {
        chartType: template.chartType,
        aggregationType: template.aggregationType,
        field: template.field,
        interval: template.aggregationType === "dateHistogram" ? "1M" : undefined,
        size: template.aggregationType === "terms" ? 10 : undefined,
      },
    };
    onAdd(widget);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Widget</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose a template to add a new widget, or ask Archie to create one for you.
        </Typography>
        <List>
          {WIDGET_TEMPLATES.map((template) => (
            <ListItemButton
              key={template.label}
              onClick={() => handleTemplateClick(template)}
              sx={{ borderRadius: 1, mb: 0.5 }}
            >
              <ListItemIcon>{template.icon}</ListItemIcon>
              <ListItemText
                primary={template.label}
                secondary={template.description}
              />
            </ListItemButton>
          ))}
          <ListItemButton
            onClick={() => {
              onClose();
              onAskArchie();
            }}
            sx={{
              borderRadius: 1,
              mt: 1,
              bgcolor: "primary.50",
              "&:hover": { bgcolor: "primary.100" },
            }}
          >
            <ListItemIcon>
              <SmartToy color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="Ask Archie"
              secondary="Describe what you want in natural language and Archie will create it"
              primaryTypographyProps={{ color: "primary" }}
            />
          </ListItemButton>
        </List>
      </DialogContent>
    </Dialog>
  );
}
